"use client";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Wrench, BookOpen } from "lucide-react";
import type { ToolUsageData } from "../types";

interface ToolUsageSectionProps {
  data: ToolUsageData;
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)} ms`;
  return `${(ms / 1000).toFixed(1)} s`;
}

export function ToolUsageSection({ data }: ToolUsageSectionProps) {
  const kbRate = data.kbTotal > 0 ? (data.kbHits / data.kbTotal) * 100 : 0;
  const totalToolCalls = data.tools.reduce((sum, t) => sum + t.calls, 0);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* Left: Tool calls table */}
      <Card className="lg:col-span-2">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <Wrench className="w-4 h-4" />
            <span>Tool Usage</span>
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            External tool calls made by the agent during email processing
          </p>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead>Tool</TableHead>
                <TableHead className="text-right">Calls</TableHead>
                <TableHead className="text-right">Success Rate</TableHead>
                <TableHead className="text-right">Avg Duration</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.tools.map((tool) => (
                <TableRow key={tool.tool}>
                  <TableCell>
                    <Badge variant="outline" className="font-medium">
                      {tool.label}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right tabular-nums font-medium">
                    {tool.calls.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    <span
                      className={
                        tool.successRate >= 0.95
                          ? "text-green-600"
                          : tool.successRate >= 0.85
                            ? "text-yellow-600"
                            : "text-red-600"
                      }
                    >
                      {(tool.successRate * 100).toFixed(0)}%
                    </span>
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">
                    {formatDuration(tool.avgDurationMs)}
                  </TableCell>
                </TableRow>
              ))}
              {data.tools.length > 1 && (
                <TableRow className="border-t-2">
                  <TableCell className="font-medium">Total</TableCell>
                  <TableCell className="text-right tabular-nums font-medium">
                    {totalToolCalls.toLocaleString()}
                  </TableCell>
                  <TableCell />
                  <TableCell />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Right: Knowledge Base stats */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <BookOpen className="w-4 h-4" />
            <span>Knowledge Base</span>
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            Emails where KB context was used in response generation
          </p>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <div className="text-3xl font-bold">{data.kbHits.toLocaleString()}</div>
              <div className="text-sm text-muted-foreground">emails with KB context</div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-muted-foreground">KB Hit Rate</span>
                <span className="text-sm font-medium tabular-nums">
                  {kbRate.toFixed(0)}%
                </span>
              </div>
              <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full rounded-full bg-blue-500 transition-all"
                  style={{ width: `${Math.min(kbRate, 100)}%` }}
                />
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {data.kbHits.toLocaleString()} <span>of</span> {data.kbTotal.toLocaleString()} <span>AI-processed emails</span>
              </div>
            </div>

            <div className="border-t pt-3">
              <div className="text-xs text-muted-foreground">
                Includes RAG similarity search and direct knowledge injection.
                Higher rate means more replies are backed by your knowledge base content.
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
